import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import SolutionCard from '../components/solutions/SolutionCard';
import TierSelector from '../components/solutions/TierSelector';
import { Tab } from '@headlessui/react';
import {
  FilterIcon,
} from '@heroicons/react/outline';
import { SolutionListItem, SolutionTier, Challenge, Sector } from '../types/solution.types';
// Service functions are now async
import { 
  getAllChallenges, 
  getAllSectors, 
  getFilteredSolutions 
} from '../services/solution.service'; 

// Helper to check if a string is a valid Challenge type
const isChallenge = (value: string): value is Challenge => {
  // Add all possible Challenge values here based on your type definition
  const validChallenges: Challenge[] = [
    'Limited Market Reach',
    'High Operating Costs',
    'Lack of Skilled Labor',
    'Regulatory Compliance',
    'Cash Flow Management',
    'Insufficient Marketing Skills',
    'Poor Infrastructure',
    'Lack of Business Skills',
    'Competition from Larger Companies',
    'Understanding the Business Environment',
    'Inefficiencies'
  ];
  return validChallenges.includes(value as Challenge);
};

// Helper to check if a string is a valid Sector type
const isSector = (value: string): value is Sector => {
  const validSectors: Sector[] = [
    'Business',
    'Retail',
    'Marketing',
    'Finance',
    'Logistics',
    'Micro-manufacturing',
    'Professional Services',
    'Manufacturing'
  ];
  return validSectors.includes(value as Sector);
};

// Helper to check if a string is a valid SolutionTier type
const isTier = (value: string): value is SolutionTier => {
  const validTiers: SolutionTier[] = ['bronze', 'silver', 'gold'];
  return validTiers.includes(value as SolutionTier);
};

const SolutionsPage: React.FC = () => {
  const location = useLocation();
  
  // Filters State - Initialize to 'all'
  const [selectedTier, setSelectedTier] = useState<SolutionTier | 'all'>('all');
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | 'all'>('all');
  const [selectedSector, setSelectedSector] = useState<Sector | 'all'>('all');
  const [activeCategory, setActiveCategory] = useState<'saas' | 'generic'>('saas');
  
  // Data State
  const [filteredSolutions, setFilteredSolutions] = useState<SolutionListItem[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  
  // Loading States
  const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(true);
  const [isLoadingSolutions, setIsLoadingSolutions] = useState<boolean>(true);

  // Effect to initialize filters from URL search params on load/change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const initialTierParam = params.get('tier');
    const initialChallengeParam = params.get('challenge');
    const initialSectorParam = params.get('sector');
    const initialCategoryParam = params.get('category');

    const tier: SolutionTier | 'all' = initialTierParam && isTier(initialTierParam) ? initialTierParam : 'all';
    const challenge: Challenge | 'all' = initialChallengeParam && isChallenge(initialChallengeParam) ? initialChallengeParam : 'all';
    const sector: Sector | 'all' = initialSectorParam && isSector(initialSectorParam) ? initialSectorParam : 'all';
    const category: 'saas' | 'generic' = initialCategoryParam === 'generic' ? 'generic' : 'saas';

    // Update state only if it differs from the current state to avoid unnecessary re-renders/fetches
    setSelectedTier(prev => prev !== tier ? tier : prev);
    setSelectedChallenge(prev => prev !== challenge ? challenge : prev);
    setSelectedSector(prev => prev !== sector ? sector : prev);
    setActiveCategory(prev => prev !== category ? category : prev);
    
  }, [location.search]); // Depend only on location.search

  // Fetch initial filter data (challenges, sectors) - Runs once
  useEffect(() => {
    const fetchFilterData = async () => {
      setIsLoadingFilters(true);
      try {
        const [fetchedChallenges, fetchedSectors] = await Promise.all([
          getAllChallenges(),
          getAllSectors()
        ]);
        setChallenges(fetchedChallenges);
        setSectors(fetchedSectors);
      } catch (error) {
        console.error("Failed to load filter data:", error);
      } finally {
        setIsLoadingFilters(false);
      }
    };
    fetchFilterData();
  }, []); // Run once on mount
  
  // Fetch filtered solutions when filters change & update URL
  useEffect(() => {
    // Check if filters are still loading; if so, wait.
    if (isLoadingFilters) return;
    
    const fetchSolutions = async () => {
      setIsLoadingSolutions(true);
      try {
        // Ensure the fetch uses the latest state values
        const solutions = await getFilteredSolutions(selectedTier, selectedChallenge, selectedSector);
        setFilteredSolutions(solutions);
      } catch (error) {
        console.error("Failed to load solutions:", error);
      } finally {
        setIsLoadingSolutions(false);
      }
    };
    fetchSolutions();
    
    // Update URL with current filters
    const params = new URLSearchParams();
    if (selectedTier !== 'all') params.set('tier', selectedTier);
    if (selectedChallenge !== 'all') params.set('challenge', selectedChallenge);
    if (selectedSector !== 'all') params.set('sector', selectedSector);
    if (activeCategory !== 'saas') params.set('category', activeCategory);
    
    const queryString = params.toString();
    const newUrl = queryString ? `${location.pathname}?${queryString}` : location.pathname;
    
    // Use replaceState to avoid adding multiple history entries
    window.history.replaceState(null, '', newUrl);
    
  // Depend on filter states and ensure filters are loaded
  }, [selectedTier, selectedChallenge, selectedSector, activeCategory, isLoadingFilters, location.pathname]); 

  const displayedSolutions = filteredSolutions.filter(solution => {
    if (activeCategory === 'saas') {
      return !!solution.productUrl;
    } else {
      return !solution.productUrl;
    }
  });
  
  return (
    <Layout>
      {/* Hero section */}
      <section className="pt-32 pb-16 bg-background dark:bg-dark-background border-b border-border/30 dark:border-dark-border/30">
        <div className="px-4 mx-auto max-w-5xl sm:px-6 lg:px-8 text-center">
          <div className="space-y-4">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-accent font-heading">Our Systems</span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground dark:text-dark-foreground">
              Solutions <span className="font-serif italic text-accent font-light">Catalogue</span>
            </h1>
            <p className="max-w-2xl mx-auto text-sm text-foreground/60 dark:text-dark-foreground/60 font-light leading-relaxed">
              Explore our range of custom corporate platforms and workflow automations engineered specifically for South African SMMEs.
            </p>
          </div>
        </div>
      </section>
      
      {/* Filters and solutions - Use background */}
      <section className="py-12 bg-background dark:bg-dark-background">
        <div className="px-4 mx-auto max-w-5xl sm:px-6 lg:px-8">
          {/* Category Selection Tabs */}
          <div className="mb-12 flex border-b border-border dark:border-dark-border justify-center">
            <button
              onClick={() => setActiveCategory('saas')}
              className={`pb-4 px-6 border-b-2 font-bold text-sm tracking-wider uppercase outline-none transition-colors ${
                activeCategory === 'saas'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-foreground/50 hover:text-foreground hover:border-border dark:text-dark-foreground/50 dark:hover:text-dark-foreground'
              }`}
            >
              SaaS Platforms <span className="ml-1.5 text-[9px] bg-accent/15 px-2 py-0.5 rounded-full text-accent font-semibold tracking-wide">Live & Beta</span>
            </button>
            <button
              onClick={() => setActiveCategory('generic')}
              className={`pb-4 px-6 border-b-2 font-bold text-sm tracking-wider uppercase outline-none transition-colors ${
                activeCategory === 'generic'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-foreground/50 hover:text-foreground hover:border-border dark:text-dark-foreground/50 dark:hover:text-dark-foreground'
              }`}
            >
              Custom Systems & Concepts
            </button>
          </div>

          {/* Tier selection */}
          <div className="mb-10">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-accent font-heading">
              Service Tiers
            </h2>
            <TierSelector 
              selectedTier={selectedTier} 
              onChange={(tier) => setSelectedTier(tier as SolutionTier | 'all')}
            />
          </div>
          
          {/* Filter tabs */}
          {isLoadingFilters ? (
             <div className="py-10 text-center">
              <div className="mx-auto spinner"></div>
              <p className="mt-4 text-xs text-muted-foreground dark:text-dark-muted-foreground">Loading Filters...</p>
            </div>
          ) : (
            <div className="mb-10">
              <div className="mb-4 sm:flex sm:items-center">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-accent font-heading">
                  Filter Solutions
                </h2>
                <div className="flex items-center mt-1 text-xs sm:mt-0 sm:ml-4 text-foreground/50 dark:text-dark-foreground/50 font-light">
                  <FilterIcon className="w-4 h-4 mr-1 text-foreground/45 dark:text-dark-foreground/45" />
                  Find solutions that match your business needs
                </div>
              </div>
              
              <Tab.Group selectedIndex={selectedSector !== 'all' ? 1 : 0} onChange={(index) => {
                    // Reset the *other* filter type when switching tabs
                    if (index === 0) {
                      setSelectedSector('all');
                    } else {
                      setSelectedChallenge('all');
                    }
                  }}>
                <div className="border-b border-border dark:border-dark-border mb-6">
                  <Tab.List className="flex space-x-8">
                    <Tab
                      className={({ selected }) =>
                        `whitespace-nowrap pb-4 px-1 border-b-2 font-semibold text-xs tracking-wider uppercase outline-none transition-colors ${
                          selected
                            ? 'border-accent text-accent'
                            : 'border-transparent text-foreground/50 hover:text-foreground hover:border-border dark:text-dark-foreground/50 dark:hover:text-dark-foreground dark:hover:border-dark-border'
                        }`
                      }
                    >
                      By Challenge
                    </Tab>
                    <Tab
                      className={({ selected }) =>
                        `whitespace-nowrap pb-4 px-1 border-b-2 font-semibold text-xs tracking-wider uppercase outline-none transition-colors ${
                          selected
                            ? 'border-accent text-accent'
                            : 'border-transparent text-foreground/50 hover:text-foreground hover:border-border dark:text-dark-foreground/50 dark:hover:text-dark-foreground dark:hover:border-dark-border'
                        }`
                      }
                    >
                      By Sector
                    </Tab>
                  </Tab.List>
                </div>
                
                <Tab.Panels className="mt-4">
                  <Tab.Panel>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedChallenge('all')}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                          selectedChallenge === 'all'
                            ? 'bg-foreground text-background border-transparent dark:bg-dark-foreground dark:text-dark-background'
                            : 'bg-transparent text-foreground/70 border-border hover:bg-secondary/60 dark:text-dark-foreground/75 dark:border-dark-border dark:hover:bg-dark-secondary/40'
                        }`}
                      >
                        All Challenges
                      </button>
                      {challenges.map((challenge: Challenge) => (
                        <button
                          key={challenge}
                          onClick={() => setSelectedChallenge(challenge)}
                          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                            selectedChallenge === challenge
                              ? 'bg-foreground text-background border-transparent dark:bg-dark-foreground dark:text-dark-background'
                              : 'bg-transparent text-foreground/70 border-border hover:bg-secondary/60 dark:text-dark-foreground/75 dark:border-dark-border dark:hover:bg-dark-secondary/40'
                          }`}
                        >
                          {challenge}
                        </button>
                      ))}
                    </div>
                  </Tab.Panel>
                  
                  <Tab.Panel>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedSector('all')}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                          selectedSector === 'all'
                            ? 'bg-foreground text-background border-transparent dark:bg-dark-foreground dark:text-dark-background'
                            : 'bg-transparent text-foreground/70 border-border hover:bg-secondary/60 dark:text-dark-foreground/75 dark:border-dark-border dark:hover:bg-dark-secondary/40'
                        }`}
                      >
                        All Sectors
                      </button>
                      {sectors.map((sector: Sector) => (
                        <button
                          key={sector}
                          onClick={() => setSelectedSector(sector)}
                          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                            selectedSector === sector
                              ? 'bg-foreground text-background border-transparent dark:bg-dark-foreground dark:text-dark-background'
                              : 'bg-transparent text-foreground/70 border-border hover:bg-secondary/60 dark:text-dark-foreground/75 dark:border-dark-border dark:hover:bg-dark-secondary/40'
                          }`}
                        >
                          {sector}
                        </button>
                      ))}
                    </div>
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          )}
          
          {/* Solutions grid */}
          {isLoadingSolutions ? (
            <div className="py-10 text-center">
              <div className="mx-auto spinner"></div>
              <p className="mt-4 text-muted-foreground dark:text-dark-muted-foreground">Loading Solutions...</p>
            </div>
          ) : (
            <AnimatePresence>
              <motion.div 
                className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {displayedSolutions.length > 0 ? (
                  displayedSolutions.map((solution) => (
                    <SolutionCard key={solution.id} {...solution} />
                  ))
                ) : (
                  <div className="py-10 text-center col-span-full">
                    <p className="text-lg text-muted-foreground dark:text-dark-muted-foreground">No solutions match the selected filters.</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default SolutionsPage;
